'use client';
import { useState, useEffect, useCallback } from 'react';
import useHuddle01 from './useHuddle01';
import type { RoomEvents } from '@huddle01/web-core/types';

export const useRoomMetadata = <T = unknown>(props?: {
  onMetadataUpdated?: (metadata: T) => void;

  onLeave?: (data: RoomEvents['room-closed'][0]) => void;
}) => {
  const { huddleClient } = useHuddle01();

  const room = huddleClient.room;

  const [roomData, setRoomData] = useState<T | null>(
    room.getMetadata<T>() ?? null,
  );

  const handleUpdateMetadata = useCallback(() => {
    const metadata = room.getMetadata<T>();

    setRoomData(metadata);

    if (props?.onMetadataUpdated) props.onMetadataUpdated(metadata);
  }, [props?.onMetadataUpdated]);

  useEffect(() => {
    const handleRoomClose = (data: RoomEvents['room-closed'][0]) => {
      setRoomData(null);

      if (props?.onLeave) props.onLeave(data);
    };

    const handleRoomJoin = () => {
      console.info('EVENT TRIGGERED, ROOM JOINED');
    };

    room.once('room-joined', handleRoomJoin);

    room.once('room-closed', handleRoomClose);

    room.on('metadata-updated', handleUpdateMetadata);

    return () => {
      room.off('room-joined', handleRoomJoin);

      room.off('room-closed', handleRoomClose);

      room.off('metadata-updated', handleUpdateMetadata);
    };
  }, [handleUpdateMetadata]);

  const updateMetadata = room.updateMetadata<T>;

  return {
    roomData,
    updateMetadata,
  };
};
